import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { ProviderPreviewMetadataComponent } from '../../../shared/components/contribution-steps/preview-metadata/preview-metadata.component';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AuthApiService, CatalogueApiService, OgcApiService } from '@angular-workspace/services';
import { inferOgcRasterFromCatalogueItem, stacItemHasAssets, uploadMethodFromItemStatus } from '@angular-workspace/feature';
import { ToasterService } from '../../../services/toaster.service';
import { ActionDropdownOverlayController } from '../../../shared/utils/action-dropdown-overlay.sync';
import { SingleRowExpansionState } from '../../../shared/utils/single-row-expansion.util';
import { displayValueOrDash } from '../../../shared/utils/display-value.util';
import {
  buildAssetCataloguePayloadWithAuth,
  normalizeAssetDescriptorList,
  resolveModifyAssetDescriptors,
} from '../../../shared/utils/asset-catalogue-payload.util';

export interface PublishAssetData {
  assetName: string;
  assetType: string;
  permission: string;
  assetStatus: string;
  publishedOn: string;
  action: string;
  originalData?: any;
}

@Component({
  selector: 'app-publish-assets',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ModalComponent,
    ProviderPreviewMetadataComponent,
    ActionDropdownTriggerDirective,
    ConfirmationDialogComponent,
  ],
  templateUrl: './publish-assets.component.html',
  styleUrl: './publish-assets.component.scss',
})
export class PublishAssetsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('publishAssetActionCell', { static: false }) publishAssetActionCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetPermissionCell', { static: false }) publishAssetPermissionCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetPublishedOnCell', { static: false }) publishAssetPublishedOnCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetExpandCell', { static: false }) publishAssetExpandCellTemplate!: TemplateRef<any>;
  @ViewChild('publishAssetChildRow', { static: false }) publishAssetChildRowTemplate!: TemplateRef<any>;

  constructor(
    private catalogueApiService: CatalogueApiService,
    private authApiService: AuthApiService,
    private ogcApiService: OgcApiService,
    private router: Router,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  publishAssetColumns: TableColumn[] = [
    { key: 'expand', label: '', sortable: false, width: '40px' },
    { key: 'assetName', label: 'Asset Name', sortable: true },
    { key: 'assetType', label: 'Asset Type', sortable: false },
    { key: 'permission', label: 'Permission', sortable: true },
    { key: 'publishedOn', label: 'Published On', sortable: true },
    { key: 'action', label: 'Action', sortable: false, fixed: 'right', width: '120px' },
  ];

  publishAssetData: PublishAssetData[] = [];
  publishAssetPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  publishAssetLoading = false;
  publishAssetEmptyMessage = 'No publish assets found';

  // Sort configuration
  currentSort: { field: string; order: 'asc' | 'desc' } = { field: 'publishedOn', order: 'desc' };

  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } = {
    top: 0,
    left: null,
    right: null,
  };
  
  // Track expanded row for child row functionality (one at a time)
  private readonly rowExpansion = new SingleRowExpansionState();

  // Modal state for modify asset
  showModifyModal: boolean = false;
  currentModifyAsset: PublishAssetData | null = null;
  modifyAssetData: any = null;
  isLoadingAssetData: boolean = false;

  /** Delete asset confirmation modal */
  showDeleteConfirmModal = false;
  rowToDelete: PublishAssetData | null = null;
  isDeleting = false;

  // Tab state: Approved = VERIFIED (+ SFTP/API), Pending = UNVERIFIED (+ SFTP/API), Rejected = DECLINED (+ SFTP/API)
  activeTab: 'approved' | 'pending' | 'rejected' = 'approved';

  /** Total count per tab (from API paginationInfo.totalCount) */
  tabCounts: { approved: number; pending: number; rejected: number } = {
    approved: 0,
    pending: 0,
    rejected: 0
  };

  /** Count for the currently active tab (displayed as Total Count on the right of tabs) */
  get totalTabCount(): number {
    switch (this.activeTab) {
      case 'approved':
        return this.tabCounts.approved ?? 0;
      case 'pending':
        return this.tabCounts.pending ?? 0;
      case 'rejected':
        return this.tabCounts.rejected ?? 0;
      default:
        return 0;
    }
  }

  ngOnInit(): void {
    this.loadPublishAssets();
    this.loadAllTabCounts();
  }

  /** Fetch totalCount for all three tabs so counts show without switching. */
  private loadAllTabCounts(): void {
    const sort = 'itemCreatedAt:desc';
    Promise.all([
      this.catalogueApiService.OrgPublishAssets(
        1,
        1,
        sort,
        ['VERIFIED', 'VERIFIED-SFTP', 'VERIFIED-API'],
        'itemStatus',
      ),
      this.catalogueApiService.OrgPublishAssets(
        1,
        1,
        sort,
        ['UNVERIFIED', 'UNVERIFIED-SFTP', 'UNVERIFIED-API'],
        'itemStatus',
      ),
      this.catalogueApiService.OrgPublishAssets(
        1,
        1,
        sort,
        ['DECLINED', 'DECLINED-SFTP', 'DECLINED-API'],
        'itemStatus',
      ),
    ]).then(([approvedRes, pendingRes, rejectedRes]) => {
      this.tabCounts.approved = this.getTotalCountFromResponse(approvedRes);
      this.tabCounts.pending = this.getTotalCountFromResponse(pendingRes);
      this.tabCounts.rejected = this.getTotalCountFromResponse(rejectedRes);
      this.cdr.detectChanges();
    }).catch(() => {
      // On error, counts stay 0; main table load already updates active tab count
    });
  }

  private getTotalCountFromResponse(response: any): number {
    if (!response) return 0;
    return response['paginationInfo']?.totalCount ?? response['totalCount'] ?? response['total'] ?? 0;
  }

  /** Collapse all child rows (e.g. when switching publish status tabs). */
  collapseExpandedRows(): void {
    this.rowExpansion.collapseAll();
    this.closeActionDropdown();
  }

  setActiveTab(tab: 'approved' | 'pending' | 'rejected'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.collapseExpandedRows();
    this.activeTab = tab;
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: 1 };
    this.loadPublishAssets();
  }

  /** Get itemStatus filter based on active tab */
  private getStatusFilterForTab(): string[] {
    switch (this.activeTab) {
      case 'approved':
        return ['VERIFIED', 'VERIFIED-SFTP', 'VERIFIED-API'];
      case 'pending':
        return ['UNVERIFIED', 'UNVERIFIED-SFTP', 'UNVERIFIED-API'];
      case 'rejected':
        return ['DECLINED', 'DECLINED-SFTP', 'DECLINED-API'];
      default:
        return ['UNVERIFIED', 'UNVERIFIED-SFTP', 'UNVERIFIED-API'];
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.publishAssetExpandCellTemplate) {
        const publishAssetExpandColumn = this.publishAssetColumns.find(col => col.key === 'expand');
        if (publishAssetExpandColumn) {
          publishAssetExpandColumn.cellTemplate = this.publishAssetExpandCellTemplate;
        }
      }
      if (this.publishAssetActionCellTemplate) {
        const publishAssetActionColumn = this.publishAssetColumns.find(col => col.key === 'action');
        if (publishAssetActionColumn) {
          publishAssetActionColumn.cellTemplate = this.publishAssetActionCellTemplate;
        }
      }
      if (this.publishAssetPermissionCellTemplate) {
        const publishAssetPermissionColumn = this.publishAssetColumns.find(col => col.key === 'permission');
        if (publishAssetPermissionColumn) {
          publishAssetPermissionColumn.cellTemplate = this.publishAssetPermissionCellTemplate;
        }
      }
      if (this.publishAssetPublishedOnCellTemplate) {
        const publishAssetPublishedOnColumn = this.publishAssetColumns.find(col => col.key === 'publishedOn');
        if (publishAssetPublishedOnColumn) {
          publishAssetPublishedOnColumn.cellTemplate = this.publishAssetPublishedOnCellTemplate;
        }
      }
    }, 100);
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  /** Permission display label for chips (OPEN/PUBLIC -> Public, etc.). */
  getPermissionLabel(permission: string): string {
    if (!permission) return '-';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'Public';
    if (p === 'RESTRICTED') return 'Restricted';
    if (p === 'PRIVATE') return 'Private';
    return permission;
  }

  /** CSS class for permission chip. */
  getPermissionChipClass(permission: string): string {
    if (!permission) return 'contribution-chip contribution-chip--public';
    const p = (permission + '').toUpperCase();
    if (p === 'OPEN' || p === 'PUBLIC') return 'contribution-chip contribution-chip--public';
    if (p === 'RESTRICTED') return 'contribution-chip contribution-chip--restricted';
    if (p === 'PRIVATE') return 'contribution-chip contribution-chip--private';
    return 'contribution-chip';
  }

  /** Format Published On as DD-MMM-YYYY (e.g. 27-Jan-2026). */
  formatPublishedOn(dateValue: string): string {
    if (!dateValue) return '-';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return dateValue;
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /** Status display label: Pending, Active, Rejected, etc. */
  getStatusLabel(status: string): string {
    if (!status) return '-';
    const s = (status + '').toUpperCase();
    if (
      s === 'VERIFIED' ||
      s.startsWith('VERIFIED-') ||
      s === 'GRANTED' ||
      s === 'PUBLISHED' ||
      s === 'LIVE' ||
      s === 'ACTIVE'
    ) {
      return 'Active';
    }
    if (s === 'UNVERIFIED' || s === 'PENDING' || s.startsWith('UNVERIFIED-')) return 'Pending';
    if (s === 'DRAFT') return 'Draft';
    if (
      s === 'REJECTED' ||
      s === 'FAILED' ||
      s === 'DENIED' ||
      s === 'DECLINED' ||
      s.startsWith('DECLINED-')
    ) {
      return 'Rejected';
    }
    return status;
  }

  /** CSS class for status chip (Pending = yellow, Granted = green, Rejected = red). */
  getStatusChipClass(status: string): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (
      s === 'VERIFIED' ||
      s.startsWith('VERIFIED-') ||
      s === 'GRANTED' ||
      s === 'PUBLISHED' ||
      s === 'LIVE' ||
      s === 'ACTIVE'
    ) {
      return 'contribution-chip contribution-chip--status-active';
    }
    if (s === 'UNVERIFIED' || s === 'PENDING' || s.startsWith('UNVERIFIED-')) return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT') return 'contribution-chip contribution-chip--status-draft';
    if (
      s === 'REJECTED' ||
      s === 'FAILED' ||
      s === 'DENIED' ||
      s === 'DECLINED' ||
      s.startsWith('DECLINED-')
    ) {
      return 'contribution-chip contribution-chip--status-rejected';
    }
    return 'contribution-chip contribution-chip--status-default';
  }

  /** Normalize API item to PublishAssetData; handles type formatting. */
  private mapItemToPublishAsset(item: any): PublishAssetData {
    const rawType = item.type || item.assetType || item.asset_type || '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    const permissionRaw = item.permission || item.accessPolicy || item.access_policy || 'PUBLIC';
    const statusRaw = item.itemStatus || item.item_status || item.publishStatus || item.publish_status || item.status || 'UNVERIFIED';
    return {
      assetName: displayValueOrDash(item.name || item.assetName || item.asset_name),
      assetType: displayValueOrDash(this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr),
      permission: displayValueOrDash(permissionRaw),
      assetStatus: displayValueOrDash(statusRaw),
      publishedOn: item.itemCreatedAt || item.item_created_at || item.createdAt || item.created_at || '',
      action: '',
      originalData: item
    };
  }

  async loadPublishAssets(): Promise<void> {
    this.publishAssetLoading = true;
    try {
      const page = this.publishAssetPagination.currentPage;
      const size = this.publishAssetPagination.pageSize;
      const statuses = this.getStatusFilterForTab();
      const sort = `${this.getApiSortField(this.currentSort.field)}:${this.currentSort.order}`;

      const response = await this.catalogueApiService.OrgPublishAssets(
        page,
        size,
        sort,
        statuses,
        'itemStatus'
      );

      let mappedData: PublishAssetData[] = [];
      let totalItems = 0;

      if (response) {
        if (response['result'] && Array.isArray(response['result'])) {
          mappedData = response['result'].map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapItemToPublishAsset(item));
          totalItems = mappedData.length;
        }
      }

      this.publishAssetData = mappedData;
      this.publishAssetPagination = { 
        ...this.publishAssetPagination, 
        totalItems: totalItems 
      };
      // Update count for current tab
      this.tabCounts[this.activeTab] = totalItems;
    } catch (error) {
      console.error('Error loading publish assets:', error);
      this.publishAssetData = [];
      this.publishAssetPagination = { ...this.publishAssetPagination, totalItems: 0 };
    } finally {
      this.publishAssetLoading = false;
      // Ensure filter panel and multiselect components are properly initialized after reload
      // Use setTimeout to let Angular finish the current change detection cycle
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);
    }
  }

  onPublishAssetPageChange(page: number): void {
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: page };
    this.loadPublishAssets();
  }

  onPublishAssetPageSizeChange(pageSize: number): void {
    this.publishAssetPagination = { ...this.publishAssetPagination, pageSize: pageSize, currentPage: 1 };
    this.loadPublishAssets();
  }

  onPublishAssetSortChange(sort: { column: string; direction: 'asc' | 'desc' }): void {
    this.currentSort = { field: sort.column, order: sort.direction };
    this.publishAssetPagination = { ...this.publishAssetPagination, currentPage: 1 };
    this.loadPublishAssets();
  }

  private getApiSortField(field: string): string {
    const fieldMapping: { [key: string]: string } = {
      assetName: 'name',
      permission: 'accessPolicy',
      publishedOn: 'itemCreatedAt'
    };
    return fieldMapping[field] || field;
  }

  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();

  toggleActionDropdown(event: Event, row: PublishAssetData): void {
    event.stopPropagation();
    const rowId = row.originalData?.id || JSON.stringify(row);

    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    const button = event.currentTarget as HTMLElement;
    this.openDropdownId = rowId;
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: 4,
      minWidth: 160,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
  }

  isDropdownOpen(row: PublishAssetData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Index in publishAssetData of the row whose action dropdown is open; null when none. */
  get openPublishAssetDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.publishAssetData.findIndex(
      (r) => (r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  /**
   * Toggle row expansion for child rows
   */
  toggleRowExpansion(row: PublishAssetData): void {
    const rowId = row.originalData?.id || JSON.stringify(row);
    if (!this.rowExpansion.isExpanded(rowId)) {
      this.closeActionDropdown();
    }
    this.rowExpansion.toggle(rowId);
  }

  /**
   * Check if a row is expanded
   */
  isRowExpanded(row: PublishAssetData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.rowExpansion.isExpanded(rowId);
  }

  /**
   * Get the bound isRowExpanded function for passing to child component
   */
  getIsRowExpanded(): (row: any) => boolean {
    return (row: any) => this.isRowExpanded(row);
  }

  /** Whether to show an action in the Publish Assets dropdown based on active tab. */
  showPublishAssetAction(action: 'view' | 'approve' | 'reject' | 'modify' | 'delete'): boolean {
    switch (action) {
      case 'view':
        return true;
      case 'approve':
      case 'reject':
        return this.activeTab === 'pending';
      case 'modify':
      case 'delete':
        return this.activeTab === 'approved';
      default:
        return false;
    }
  }

  handleAction(action: string, row: PublishAssetData): void {
    this.closeActionDropdown();
    console.log(`${action} action for:`, row);

    if (action === 'approve') {
      this.approvePublishAsset(row);
    } else if (action === 'reject') {
      this.rejectPublishAsset(row);
    } else if (action === 'view') {
      this.viewPublishAsset(row);
    } else if (action === 'modify') {
      this.openModifyModal(row);
    } else if (action === 'delete') {
      this.rowToDelete = row;
      this.showDeleteConfirmModal = true;
    }
  }

  onConfirmDelete(): void {
    if (!this.rowToDelete) return;
    void this.performDeletePublishAsset(this.rowToDelete);
  }

  onCancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.rowToDelete = null;
  }

  /**
   * Approve/reject itemStatus: SFTP/API rows become VERIFIED-SFTP|VERIFIED-API or
   * DECLINED-SFTP|DECLINED-API; others stay VERIFIED / DECLINED.
   */
  private resolveOrgAdminDecisionItemStatus(
    row: PublishAssetData,
    decision: 'VERIFIED' | 'DECLINED',
  ): string {
    const current = String(
      row.originalData?.itemStatus ??
        row.originalData?.item_status ??
        row.assetStatus ??
        '',
    ).trim();
    const method = uploadMethodFromItemStatus(current);
    if (method === 'SFTP' || method === 'API') {
      return `${decision}-${method}`;
    }
    return decision;
  }

  private async approvePublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      console.warn('Unable to approve asset: missing asset id', row);
      this.toasterService.error('Unable to approve asset: missing asset ID');
      return;
    }

    try {
      const payload: { itemStatus: string; dataUploadStatus?: boolean } = {
        itemStatus: this.resolveOrgAdminDecisionItemStatus(row, 'VERIFIED'),
      };

      if (inferOgcRasterFromCatalogueItem(row.originalData)) {
        payload.dataUploadStatus = await stacItemHasAssets(
          this.ogcApiService,
          String(assetId),
        );
      }

      await this.catalogueApiService.postPublishAssets(assetId, payload);

      const assetLabel = (row.assetType || 'asset').trim().toLowerCase() || 'asset';
      const publishStatus = String(
        row.originalData?.publishStatus ?? row.originalData?.publish_status ?? '',
      )
        .toUpperCase()
        .trim();
      const dataUploadRaw =
        payload.dataUploadStatus ??
        row.originalData?.dataUploadStatus ??
        row.originalData?.data_upload_status;
      const hasFileUpload = dataUploadRaw === true || dataUploadRaw === 'true';
      const platformAdminApproved =
        publishStatus === 'ACTIVE' ||
        publishStatus === 'GRANTED' ||
        publishStatus === 'PUBLISHED' ||
        publishStatus === 'LIVE';

      // Always confirm org-admin approval first; follow with any next-step warning.
      this.toasterService.success('Asset has been approved successfully');
      let followUpWarning: string | null = null;
      if (publishStatus === 'DECLINED' || publishStatus === 'REJECTED') {
        // Platform admin reject → asset will not publish.
        followUpWarning = `This request has been rejected by the Platform Administrator. The ${assetLabel} has not been published.`;
      } else if (platformAdminApproved && !hasFileUpload) {
        // Both admins approved but upload incomplete — same warning as platform admin.
        followUpWarning = `Please upload a file to publish this ${assetLabel}.`;
      }
      if (followUpWarning) {
        setTimeout(() => {
          this.toasterService.warning(followUpWarning!, 5000);
        }, 500);
      }

      // Optimistic update — remove row immediately so the Pending tab reflects the
      // change without waiting for the backend index to catch up.
      this.publishAssetData = this.publishAssetData.filter(r => r !== row);
      this.publishAssetPagination = {
        ...this.publishAssetPagination,
        totalItems: Math.max(0, this.publishAssetPagination.totalItems - 1)
      };
      this.tabCounts.pending = Math.max(0, this.tabCounts.pending - 1);
      this.tabCounts.approved = this.tabCounts.approved + 1;
      this.cdr.detectChanges();

      // Background sync with the server after a short delay.
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
      }, 500);
    } catch (error) {
      console.error('Error approving asset:', error);
      this.toasterService.error('Failed to approve asset. Please try again.');
    }
  }

  private async performDeletePublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      this.toasterService.error('Unable to delete asset: missing asset ID');
      this.onCancelDelete();
      return;
    }

    this.isDeleting = true;
    const assetName = row.assetName || row.originalData?.name || row.originalData?.label || '';

    try {
      await this.catalogueApiService.deleteItem(assetId);
      const successMessage = assetName
        ? `Successfully deleted asset "${assetName}"`
        : 'Asset has been deleted successfully';
      this.toasterService.success(successMessage);
      this.onCancelDelete();
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error deleting asset:', error);
      this.toasterService.error('Failed to delete asset. Please try again.');
      this.onCancelDelete();
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  private async rejectPublishAsset(row: PublishAssetData): Promise<void> {
    const assetId = row.originalData?.id;

    if (!assetId) {
      console.warn('Unable to reject asset: missing asset id', row);
      this.toasterService.error('Unable to reject asset: missing asset ID');
      return;
    }

    try {
      const payload: { itemStatus: string; dataUploadStatus?: boolean } = {
        itemStatus: this.resolveOrgAdminDecisionItemStatus(row, 'DECLINED'),
      };

      if (inferOgcRasterFromCatalogueItem(row.originalData)) {
        payload.dataUploadStatus = false;
      }

      await this.catalogueApiService.postPublishAssets(assetId, payload);
      
      this.toasterService.success('Asset has been rejected successfully');

      // Optimistic update — remove row immediately so the Pending tab reflects the
      // change without waiting for the backend index to catch up.
      this.publishAssetData = this.publishAssetData.filter(r => r !== row);
      this.publishAssetPagination = {
        ...this.publishAssetPagination,
        totalItems: Math.max(0, this.publishAssetPagination.totalItems - 1)
      };
      this.tabCounts.pending = Math.max(0, this.tabCounts.pending - 1);
      this.tabCounts.rejected = this.tabCounts.rejected + 1;
      this.cdr.detectChanges();

      // Background sync with the server after a short delay.
      setTimeout(() => {
        this.loadPublishAssets();
        this.loadAllTabCounts();
      }, 500);
    } catch (error) {
      console.error('Error rejecting asset:', error);
      this.toasterService.error('Failed to reject asset. Please try again.');
    }
  }

  private viewPublishAsset(row: PublishAssetData): void {
    const original = row.originalData || {};
    const assetId = original.id || original['assetId'] || original['datasetId'] || original['modelId'];

    if (!assetId) {
      console.warn('Unable to navigate to asset details: missing asset id', row);
      this.toasterService.error('Unable to view asset: missing asset ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType = original.type || original.assetType || original.asset_type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();
      
      if (rawTypeLower.includes('databank') || rawTypeLower.includes('database')) {
        routeSegment = 'dataset';
      } else if (rawTypeLower.includes('aimodel') || rawTypeLower.includes('model')) {
        routeSegment = 'model';
      } else if (rawTypeLower.includes('app') || rawTypeLower.includes('usecase')) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      // Open in new tab
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, assetId])
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error('Unable to determine asset type for navigation');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  /**
   * Open modify modal and load asset data
   */
  async openModifyModal(row: PublishAssetData): Promise<void> {
    console.log('=== PUBLISH-ASSETS: openModifyModal CALLED ===');
    console.log('PUBLISH-ASSETS: row:', row);
    
    const assetId = row.originalData?.id;
    console.log('PUBLISH-ASSETS: assetId:', assetId);

    if (!assetId) {
      console.warn('PUBLISH-ASSETS: Unable to modify asset - missing asset id', row);
      this.toasterService.error('Unable to modify asset: missing asset ID');
      return;
    }

    this.isLoadingAssetData = true;
    this.currentModifyAsset = row;

    try {
      // Fetch full asset details
      console.log('PUBLISH-ASSETS: Fetching asset details...');
      const response = await this.catalogueApiService.getItemById(assetId);
      console.log('PUBLISH-ASSETS: getItemById response:', response);
      
      // Extract asset data from response
      let assetData = response;
      if (response?.result) {
        assetData = Array.isArray(response.result) ? response.result[0] : response.result;
      } else if (response?.data) {
        assetData = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        assetData = response[0];
      }

      if (!assetData) {
        throw new Error('No asset data found in response');
      }

      console.log('PUBLISH-ASSETS: Extracted assetData:', assetData);

      this.currentModifyAsset = {
        ...row,
        originalData: {
          ...(row.originalData || {}),
          ...assetData,
        },
      };

      // Map asset data to preview-metadata format
      this.modifyAssetData = this.mapAssetDataToPreviewFormat(assetData, row.assetType);
      console.log('PUBLISH-ASSETS: Mapped modifyAssetData:', this.modifyAssetData);
      
      this.showModifyModal = true;
      console.log('PUBLISH-ASSETS: Modal opened, showModifyModal:', this.showModifyModal);
    } catch (error) {
      console.error('PUBLISH-ASSETS: Error loading asset data for modification:', error);
      this.toasterService.error('Failed to load asset data. Please try again.');
      this.closeModifyModal();
    } finally {
      this.isLoadingAssetData = false;
    }
  }

  /**
   * Close modify modal
   */
  closeModifyModal(): void {
    this.showModifyModal = false;
    this.currentModifyAsset = null;
    this.modifyAssetData = null;
    this.isLoadingAssetData = false;
  }

  /**
   * Get asset type for modify modal
   */
  getAssetTypeForModify(): 'dataset' | 'model' | null {
    if (!this.currentModifyAsset) return null;
    const typeLabel = (this.currentModifyAsset.assetType || '').toLowerCase();
    if (typeLabel === 'model') return 'model';
    if (typeLabel === 'dataset') return 'dataset';
    return null;
  }

  /**
   * Ensure minimum length for validation (pads with spaces if needed)
   */
  private ensureMinLength(value: string, minLength: number = 50): string {
    if (!value) return '';
    return value.length < minLength ? value.padEnd(minLength, ' ') : value;
  }

  /**
   * Map API response to preview-metadata expected format
   */
  private mapAssetDataToPreviewFormat(apiData: any, assetType: string): any {
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - apiData:', apiData);
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - assetType:', assetType);

    const isModel = assetType.toLowerCase().includes('model') ||
                    (apiData.type && Array.isArray(apiData.type) && apiData.type.some((t: string) => t.includes('AiModel'))) ||
                    (apiData.type && typeof apiData.type === 'string' && apiData.type.includes('AiModel'));

    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - isModel:', isModel);

    // Map access policy
    const accessPolicy = apiData.accessPolicy || apiData.access_policy || 'OPEN';
    const permission = accessPolicy === 'OPEN' ? 'Open' : 
                       accessPolicy === 'RESTRICTED' ? 'Restricted' : 'Private';

    // Parse year range - handle both string and object formats
    let yearRange = apiData.yearRange || apiData.year_range || null;
    console.log('PUBLISH-ASSETS: yearRange (raw):', yearRange);
    
    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0]) || null,
          endYear: parseInt(parts[1]) || null
        };
      }
    } else if (typeof yearRange === 'object' && yearRange !== null && !yearRange.startYear) {
      // If it's an object but doesn't have startYear/endYear, try to convert
      yearRange = null;
    }
    console.log('PUBLISH-ASSETS: yearRange (parsed):', yearRange);

    // Handle resourceServer - extract URL from array if needed
    let resourceServerUrl = '';
    const rsData = apiData.resourceServer || apiData.resource_server;
    console.log('PUBLISH-ASSETS: resourceServer (raw):', rsData);
    
    if (Array.isArray(rsData) && rsData.length > 0) {
      resourceServerUrl = rsData[0]?.url || '';
    } else if (typeof rsData === 'string') {
      resourceServerUrl = rsData;
    } else if (rsData && typeof rsData === 'object' && rsData.url) {
      resourceServerUrl = rsData.url;
    }
    console.log('PUBLISH-ASSETS: resourceServerUrl (extracted):', resourceServerUrl);

    // Get short and long descriptions, ensuring minimum length for validation
    const shortDesc = apiData.shortDescription || apiData.short_description || '';
    const longDesc = apiData.description || apiData.longDescription || '';

    const mappedData = {
      name: apiData.name || apiData.label || '',
      title: apiData.name || apiData.label || '',
      permission: permission,
      accessPolicy: accessPolicy,
      shortDescription: this.ensureMinLength(shortDesc, 50),
      description: this.ensureMinLength(longDesc, 50),
      longDescription: this.ensureMinLength(longDesc, 50),
      license: apiData.license || 'Open Government License, India',
      otherLicense: apiData.otherLicense || apiData.other_license || '',
      resourceServer: resourceServerUrl,
      uploadFrequency: apiData.uploadFrequency || apiData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: apiData.geoCoverage || apiData.geo_coverage || '',
      industry: apiData.industry || 'Agriculture',
      datasetType: isModel
        ? (apiData.modelType || apiData.model_type || apiData.datasetType || '')
        : (apiData.dataBankType || apiData.datasetType || 'Structured'),
      fileFormat: apiData.fileFormat || apiData.file_format || 'csv',
      tags: Array.isArray(apiData.tags) ? apiData.tags : (apiData.tags ? [apiData.tags] : []),
      sampleDataUrl: (() => {
        const raw = apiData.mediaURL || apiData.media_url || apiData.mediaUrl || apiData.sampleDataUrl || '';
        return raw === 'https://iudx.io/' ? '' : raw;
      })(),
      dataDescriptor: apiData.dataDescriptor ?? apiData.data_descriptor,
      dataDescriptors: normalizeAssetDescriptorList(
        apiData.dataDescriptor ?? apiData.data_descriptor ?? apiData.dataDescriptors ?? [],
      ),
      type: apiData.type || []
    };

    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - mappedData:', mappedData);
    console.log('PUBLISH-ASSETS: mapAssetDataToPreviewFormat - mappedData (JSON):', JSON.stringify(mappedData, null, 2));

    return mappedData;
  }

  /**
   * Handle form submission from preview-metadata component
   */
  async onModifyFormSubmit(formData: any): Promise<void> {
    console.log('=== PUBLISH-ASSETS: onModifyFormSubmit CALLED ===');
    console.log('formData:', formData);
    console.log('currentModifyAsset:', this.currentModifyAsset);

    if (!this.currentModifyAsset) {
      console.warn('PUBLISH-ASSETS: No currentModifyAsset - returning early');
      return;
    }

    const assetId = this.currentModifyAsset.originalData?.id;
    console.log('PUBLISH-ASSETS: assetId:', assetId);

    if (!assetId) {
      console.error('PUBLISH-ASSETS: Missing asset ID');
      this.toasterService.error('Unable to update asset: missing asset ID');
      return;
    }

    try {
      // Build payload for updateItem API
      console.log('PUBLISH-ASSETS: Building update payload...');
      const payload = await buildAssetCataloguePayloadWithAuth(this.authApiService, {
        formData,
        isModel: this.getAssetTypeForModify() === 'model',
        assetId,
        originalData: this.currentModifyAsset?.originalData,
        assetPreviewData: this.modifyAssetData ?? undefined,
        dataDescriptors: resolveModifyAssetDescriptors(formData, {
          assetPreviewData: this.modifyAssetData ?? undefined,
          originalData: this.currentModifyAsset?.originalData,
        }),
        includeModelDataSourceUrl: true,
      });
      console.log('PUBLISH-ASSETS: Update payload:', payload);
      console.log('PUBLISH-ASSETS: Update payload (JSON):', JSON.stringify(payload, null, 2));

      console.log('PUBLISH-ASSETS: Calling catalogueApiService.updateItem...');
      const response = await this.catalogueApiService.updateItem(payload);
      console.log('PUBLISH-ASSETS: Update response:', response);
      console.log('PUBLISH-ASSETS: Update response (JSON):', JSON.stringify(response, null, 2));
      
      this.toasterService.success('Asset metadata updated successfully');
      this.loadPublishAssets();
      this.closeModifyModal();
      console.log('=== PUBLISH-ASSETS: Update completed successfully ===');
    } catch (error) {
      console.error('=== PUBLISH-ASSETS: Error updating asset ===', error);
      this.toasterService.error('Failed to update asset. Please try again.');
    }
  }

  /**
   * Handle form cancel from preview-metadata component
   */
  onModifyFormCancel(): void {
    this.closeModifyModal();
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }
}
